import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { cloneDeep, isEqual, uniqBy } from 'lodash';
import classnames from 'classnames';
import CargomaticModal from 'components/v2/modal';
import ExceptionStore from 'stores/ExceptionStore';

import { withTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import SessionStore from 'stores/SessionStore';
import USER_TYPES from 'constants/UserTypes';
import { Disclosure } from '@headlessui/react';
import ChevronDownIcon from 'components/v2/icons/ChevronDownIcon';
import ChevronRightIcon from 'components/v2/icons/ChevronRightIcon';
import ConfirmationModalBody from './ConfirmationModalBody';
import Alert from '../../../ui/Alert';
import { getDeclinedAccessorialsReasonCodes } from '../../../../utils/ReasonCodesAPI';
import ShipmentLink from 'components/modules/carrier/ShipmentLink';
import { isShipmentCanceled } from 'helpers/shipments';
import { sluggify } from 'utils/localization/utils';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import { validDecimal } from 'utils/accounting-util';
import {
  checkWhichSideAccessorialAutoDeclined,
  mapProcessedAccessorialLedgerItem,
  isCarrierMissingInAccessorial,
  getCarrierAccordionTitle,
} from 'helpers/accessorials';
import { isVendorChargePaid } from 'common/helpers/accessorials';

const ALERT_MESSAGES = {
  carrier_mismatch: 'ACCESSORIALS_WITH_CARRIER_MISMATCH',
  carrier_missing: 'ACCESSORIALS_WITH_CARRIER_MISSING',
  carrier_mismatch_and_missing:
    'ACCESSORIALS_WITH_CARRIER_MISMATCH_AND_MISSING',
  shipment_canceled: 'ACCESSORIALS_WITH_SHIPMENT_CANCELED',
  processed_accessorials: 'ACCESSORIALS_WITH_INVOICE_OR_PAYOUT',
  auto_applied_shipper_change_decline_all:
    'AUTO_APPLIED_SHIPPER_CHANGE_DECLINED_ALL',
  auto_applied_shipper_change_approve_all:
    'AUTO_APPLIED_SHIPPER_CHANGE_APPROVE_ALL',
  auto_applied_shipper_change_notify_all:
    'AUTO_APPLIED_SHIPPER_CHANGE_NOTIFY_ALL',
};

const defaultColumnHeadersAllApprove = [
  {
    value: 'Shipment ID',
    localizationSlug: 'SHIPMENT_ID',
  },
  {
    value: 'Accessorial',
    localizationSlug: 'ACCESSORIAL',
  },
  {
    value: 'Carrier/Vendor Rate',
    localizationSlug: 'CARRIER_VENDOR_RATE',
  },
  {
    value: 'Carrier/Vendor Units',
    localizationSlug: 'CARRIER_VENDOR_UNITS',
  },
  {
    value: 'Carrier/Vendor Total',
    localizationSlug: 'CARRIER_VENDOR_TOTAL',
  },
  {
    value: 'Shipper Rate',
    localizationSlug: 'SHIPPER_RATE',
  },
  {
    value: 'Shipper Units',
    localizationSlug: 'SHIPPER_UNITS',
  },
  {
    value: 'Shipper Total',
    localizationSlug: 'SHIPPER_TOTAL',
  },
  {
    value: 'Missing Docs',
    localizationSlug: 'MISSING_DOCS',
  },
];

const defaultColumnHeadersAllDecline = [
  {
    value: 'Shipment ID',
    localizationSlug: 'SHIPMENT_ID',
  },
  {
    value: 'Accessorial',
    localizationSlug: 'ACCESSORIAL',
  },
  {
    value: 'Shipper Rate',
    localizationSlug: 'SHIPPER_RATE',
  },
  {
    value: 'Shipper Units',
    localizationSlug: 'SHIPPER_UNITS',
  },
  {
    value: 'Shipper Total',
    localizationSlug: 'SHIPPER_TOTAL',
  },
  {
    value: 'Carrier/Vendor Rate',
    localizationSlug: 'CARRIER_VENDOR_RATE',
  },
  {
    value: 'Carrier/Vendor Units',
    localizationSlug: 'CARRIER_VENDOR_UNITS',
  },
  {
    value: 'Carrier/Vendor Total',
    localizationSlug: 'CARRIER_VENDOR_TOTAL',
  },
  {
    value: 'Carrier/Vendor Note',
    localizationSlug: 'CARRIER_VENDOR_NOTE',
  },
];

const defaultColumnHeadersNotifyShipper = [
  {
    value: 'Accessorial',
    localizationSlug: 'ACCESSORIAL',
  },
  {
    value: 'Carrier/Vendor Rate',
    localizationSlug: 'CARRIER_VENDOR_RATE',
  },
  {
    value: 'Carrier/Vendor Units',
    localizationSlug: 'CARRIER_VENDOR_UNITS',
  },
  {
    value: 'Carrier/Vendor Total',
    localizationSlug: 'CARRIER_VENDOR_TOTAL',
  },
  {
    value: 'Shipper Rate',
    localizationSlug: 'SHIPPER_RATE',
  },
  {
    value: 'Shipper Units',
    localizationSlug: 'SHIPPER_UNITS',
  },
  {
    value: 'Shipper Total',
    localizationSlug: 'SHIPPER_TOTAL',
  },
  {
    value: 'Shipper Note',
    localizationSlug: 'SHIPPER_NOTE',
  },
  {
    value: 'Apply',
    localizationSlug: 'APPLY',
  },
];

const messageLookup = {
  approve_all: 'ACCESSORIAL_CONFIRMATION_MODAL_APPROVE',
  decline_all: 'ACCESSORIAL_CONFIRMATION_MODAL_DECLINE',
  notify_all: 'ACCESSORIAL_CONFIRMATION_MODAL_NOTIFY_SHIPPER',
};

const INVALID_VALUE_ERROR_MSG = 'UNIT_AND_COST_VALIDATION_ERROR_MSG';

class ConfirmationModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      accessorialUpdates: {},
      accessorials: [],
      payerChecked: true,
      payeeChecked: true,
      errors: {},
      notifyShipperErrors: {},
      disabled: false,
      isAdmin: SessionStore.isAccessorialsAdmin(),
      accessorialsReasonCodes: [],
      accessorialsWithCarrierMismatch: [],
      accessorialsWithCanceledShipments: [],
      accessorialInProcessedPayouts: [],
      accessorialInProcessedInvoices: [],
      declinedAutoAppliedAccessorialShipperChanged: [],
      accessorialsWithCarrierMissing: [],
      missingCustomerApproval: false,
      missingDocs: false,
    };
  }

  componentDidMount() {
    this.getSelectedAccessorials();
    this.getDeclineReasonCodes();
    ExceptionStore.addAccessorialRequestChangeListener(
      this.handleAccessorialRequestStoreChange,
    );
  }

  componentDidUpdate(prevProps, _prevState) {
    if (
      prevProps.selectedAccessorials.length !==
      this.props.selectedAccessorials.length
    )
      this.getSelectedAccessorials();
  }

  componentWillUnmount() {
    ExceptionStore.removeAccessorialRequestChangeListener(
      this.handleAccessorialRequestStoreChange,
    );
  }

  getModalConfig = (status) => {
    const { t } = this.props;
    const message = t(messageLookup[status]);
    switch (status) {
      case 'approve_all':
        return {
          title: titleize(t('APPROVE')),
          headers: defaultColumnHeadersAllApprove,
          message,
          actionsEnabled: true,
        };
      case 'decline_all':
        return {
          title: t('DECLINE_AND_NOTIFY_CARRIER'),
          headers: defaultColumnHeadersAllDecline,
          message,
          actionsEnabled: true,
        };
      case 'notify_all':
        return {
          title: t('NOTIFY_SHIPPER'),
          headers: defaultColumnHeadersNotifyShipper,
          message,
          actionsEnabled: true,
        };
      default:
        return null;
    }
  };

  carrierMismatchCheck = (selectedAccessorials) =>
    selectedAccessorials.filter((acc) => {
      const shipmentCarrier =
        acc.shipment?.carrier?._id || acc.shipment?.carrier;
      const carrierAssociatedWithAccessorial = acc.carrier?._id || acc.carrier;
      return (
        acc.payee_type === 'carrier' &&
        (!!carrierAssociatedWithAccessorial || !!shipmentCarrier) &&
        (carrierAssociatedWithAccessorial !== shipmentCarrier ||
          checkWhichSideAccessorialAutoDeclined(acc, 'carrier'))
      );
    });

  isCarrierMissing = (selectedAccessorials) =>
    selectedAccessorials.filter((acc) => {
      return isCarrierMissingInAccessorial(acc);
    });

  shipperSideAccessorialDeclined = (selectedAccessorials) =>
    selectedAccessorials.filter((acc) => {
      return checkWhichSideAccessorialAutoDeclined(acc, 'shipper');
    });

  customerApprovalCheck = (selectedAccessorials) => {
    if (featureEnabled(CONFIGURED_FEATURES.SSR)) {
      return (
        this.props.action === 'approve_all' &&
        selectedAccessorials.some(
          (acc) => acc.customer_approval_provided === false,
        )
      );
    }
    return false;
  };

  checkProcessedAccessorials = (selectedAccessorials = [], ledger) => {
    const results = selectedAccessorials.filter((selectedAccessorial) =>
      mapProcessedAccessorialLedgerItem(selectedAccessorial, ledger),
    );
    return results;
  };

  getSelectedAccessorials = async () => {
    const accessorials = ExceptionStore.getAllAccessorialRequests();
    const selectedAccessorials = accessorials.filter((acc) =>
      this.props.selectedAccessorials.includes(acc._id),
    );
    const accessorialUpdates = this.generateModalBodyData(selectedAccessorials);
    const accessorialsWithCarrierMismatch =
      this.carrierMismatchCheck(selectedAccessorials);
    const accessorialsWithCarrierMissing =
      this.isCarrierMissing(selectedAccessorials);
    const accessorialsWithCanceledShipments = selectedAccessorials.filter(
      (acc) => isShipmentCanceled(acc.shipment),
    );
    const missingCustomerApproval =
      this.customerApprovalCheck(selectedAccessorials);
    const accessorialInProcessedPayouts = this.checkProcessedAccessorials(
      selectedAccessorials,
      'payables',
    );
    const accessorialInProcessedInvoices = this.checkProcessedAccessorials(
      selectedAccessorials,
      'receivables',
    );
    const declinedAutoAppliedAccessorialShipperChanged =
      this.shipperSideAccessorialDeclined(selectedAccessorials);
    const missingDocs = selectedAccessorials.some((acc) => acc.missing_docs);
    const { isAdmin } = this.state;
    const { action } = this.props;

    const isPayerChecked = () => {
      const canSelect =
        !accessorialsWithCanceledShipments.length &&
        !accessorialInProcessedInvoices.length &&
        this.state.payerChecked &&
        !declinedAutoAppliedAccessorialShipperChanged?.length;

      if (canSelect && action === 'decline_all') {
        return true;
      }
      if (canSelect && action === 'notify_all') {
        return true;
      }

      // Accessorial Admin Override Missing Docs
      if (isAdmin && canSelect) {
        return true;
      }
      return canSelect && !missingCustomerApproval && !missingDocs;
    };

    const isPayeeChecked = () => {
      const canSelect =
        !accessorialsWithCarrierMismatch.length &&
        !accessorialsWithCarrierMissing.length &&
        !accessorialsWithCanceledShipments.length &&
        !accessorialInProcessedPayouts.length &&
        this.state.payeeChecked;

      if (canSelect && action === 'decline_all') {
        return true;
      }
      if (canSelect && action === 'notify_all') {
        return true;
      }
      if (isAdmin && canSelect) {
        return true;
      }
      return canSelect && !missingDocs;
    };

    const payeeChecked = isPayeeChecked();
    const payerChecked = isPayerChecked();

    this.setState({
      accessorials,
      accessorialUpdates,
      accessorialsWithCarrierMismatch,
      accessorialsWithCanceledShipments,
      accessorialInProcessedPayouts,
      accessorialInProcessedInvoices,
      declinedAutoAppliedAccessorialShipperChanged,
      accessorialsWithCarrierMissing,
      payeeChecked,
      payerChecked,
      missingCustomerApproval,
      disabled: !payeeChecked && !payerChecked,
      missingDocs,
    });
  };

  getDeclineReasonCodes = async () => {
    if (this.props.action === 'decline_all') {
      try {
        const accessorialsReasonCodes =
          await getDeclinedAccessorialsReasonCodes();
        this.setState({ accessorialsReasonCodes });
      } catch (error) {
        console.log('Unable to get decline reason codes', error);
      }
    }
  };

  generateModalBodyData = (accessorials) => {
    const { accessorialUpdates } = this.state;
    let meta =
      this.props.action === 'notify_all'
        ? { shipper_note: ' ' }
        : { carrier_note: ' ' };
    return accessorials.reduce((update, accessorial) => {
      if (accessorialUpdates[accessorial._id])
        meta = accessorialUpdates[accessorial._id].meta;
      else if (this.props.action === 'notify_all')
        meta = {
          shipper_rate: accessorial?.shipper_rate,
          shipper_unit_qty: accessorial?.shipper_unit_qty,
          shipper_note: accessorial?.shipper_note,
        };
      update[accessorial._id] = {
        ...accessorial,
        meta,
      };
      return update;
    }, {});
  };

  generateAccessorialApprovalPayload = () => {
    const { accessorialUpdates, payerChecked, payeeChecked } = this.state;
    return Object.keys(accessorialUpdates).map((_id) => {
      const accessorial = accessorialUpdates[_id];
      const {
        payee_type,
        shipper_status,
        carrier_status,
        _id: accessorialId,
        shipment: { vendor_payables = [] },
      } = accessorial;
      const isVendorPaid =
        carrier_status === 'approved' &&
        isVendorChargePaid(accessorialId, vendor_payables);
      const payer_type = accessorial.payer_type || 'shipper';
      return {
        ...accessorial,
        approveCarrier:
          payee_type === USER_TYPES.CARRIER &&
          carrier_status !== 'approved' &&
          payeeChecked,
        approveShipper:
          payer_type === USER_TYPES.SHIPPER &&
          shipper_status !== 'approved' &&
          payerChecked,
        approveVendor:
          payee_type === USER_TYPES.VENDOR && payeeChecked && !isVendorPaid,
        approveConsigneePayer:
          payer_type === USER_TYPES.CONSIGNEE && payerChecked,
        approvePartnerPayee: payee_type === USER_TYPES.PARTNER && payeeChecked,
      };
    });
  };

  generateAccessorialDeclinePayload = () => {
    const { accessorialUpdates, payeeChecked, payerChecked } = this.state;
    const accessorials = Object.keys(accessorialUpdates).reduce(
      (payload, _id) => {
        const accessorial = accessorialUpdates[_id];
        const {
          payee_type,
          payer_type,
          meta,
          carrier_status,
          _id: accessorialId,
          shipment: { vendor_payables = [] },
        } = accessorial;
        const isVendorPaid =
          carrier_status === 'approved' &&
          isVendorChargePaid(accessorialId, vendor_payables);
        if (
          meta[`${_id}_Carrier/Vendor Note`] &&
          payee_type === USER_TYPES.CARRIER &&
          payeeChecked
        ) {
          accessorial.carrierDeclineReasonCode =
            meta[`${_id}_Carrier/Vendor Note`];
        }
        payload.push({
          ...accessorial,
          declineShipper: payer_type === USER_TYPES.SHIPPER && payerChecked,
          declineCarrier: payee_type === USER_TYPES.CARRIER && payeeChecked,
          declineVendor:
            payee_type === USER_TYPES.VENDOR && payeeChecked && !isVendorPaid,
          declinePartnerPayee:
            payee_type === USER_TYPES.PARTNER && payeeChecked,
          declineConsigneePayer:
            payer_type === USER_TYPES.CONSIGNEE && payerChecked,
        });
        return payload;
      },
      [],
    );

    return {
      resolution_type: 'decline',
      accessorials,
    };
  };

  generateAccessorialNotifyPayload = () => {
    const { accessorialUpdates } = this.state;
    const updates = [];
    Object.keys(accessorialUpdates).forEach((_id) => {
      const accessorial = accessorialUpdates[_id];
      if (accessorial) {
        accessorial.meta.shipper_rate =
          accessorial?.meta?.shipper_rate || accessorial?.shipper_rate || 0;
        accessorial.meta.shipper_unit_qty =
          parseFloat(
            accessorial.meta.shipper_unit_qty || accessorial.shipper_unit_qty,
          ) || 1;
        accessorial.meta.carrier_unit_qty =
          parseFloat(
            accessorial.meta.carrier_unit_qty || accessorial.carrier_unit_qty,
          ) || 1;
        updates.push({
          _id: accessorial._id,
          shipper_status: 'pending-shipper',
          ...accessorial.meta,
        });
      }
    });
    return updates;
  };

  handleTogglePayeeChecked = (payeeChecked) => {
    const { isAdmin, missingDocs } = this.state;
    if ((missingDocs && isAdmin) || !missingDocs) {
      return this.setState({ payeeChecked, disabled: false, errors: {} });
    }
  };

  handleTogglePayerChecked = (payerChecked) => {
    const { isAdmin, missingCustomerApproval, missingDocs } = this.state;

    if (missingDocs && isAdmin) {
      this.setState({ payerChecked, disabled: false, errors: {} });
    } else if (!missingCustomerApproval && !missingDocs) {
      this.setState({ payerChecked, disabled: false, errors: {} });
    }
  };

  handleAccessorialRequestStoreChange = () => {
    const accessorials = ExceptionStore.getAllAccessorialRequests();
    const selectedAccessorials = accessorials.filter((acc) =>
      this.props.selectedAccessorials.includes(acc._id),
    );
    if (!isEqual(this.state.accessorials, selectedAccessorials)) {
      if (accessorials && accessorials.length) {
        const accessorialUpdates =
          this.generateModalBodyData(selectedAccessorials);
        this.setState({
          accessorials: selectedAccessorials,
          accessorialUpdates,
          disabled: false,
        });
      }
    }
  };

  hasUnselectedDeclineReasonCodes = () => {
    const { accessorialUpdates, payeeChecked } = this.state;
    if (!accessorialUpdates) {
      return false;
    }
    const updates = Object.entries(accessorialUpdates);
    return updates.some(
      (u) =>
        u[1].payee_type === USER_TYPES.CARRIER &&
        payeeChecked &&
        !u[1].meta[`${u[0]}_Carrier/Vendor Note`],
    );
  };

  invalidUnitQty = (field, value) =>
    field.indexOf('unit_qty') > -1 && !validDecimal(value);

  invalidRate = (field, value) =>
    field.indexOf('rate') > -1 && !validDecimal(value);

  handleAccessorialChange = (accessorialId, accessorialUpdate, field) => {
    const { t } = this.props;
    this.setState((prevState) => {
      let errorObj = {};
      if (
        accessorialUpdate[field] < 0 ||
        this.invalidUnitQty(field, accessorialUpdate[field]) ||
        this.invalidRate(field, accessorialUpdate[field])
      ) {
        errorObj = {
          [`${accessorialId}_${field}`]: t(INVALID_VALUE_ERROR_MSG, {
            userType: t('SHIPPER'),
          }),
        };
      }

      const { accessorialUpdates: currentUpdates, errors: currentErrors } =
        prevState;

      // check if the error exist and remove it
      const errorKey = `${accessorialId}_${field}`;
      if (errorKey in currentErrors) {
        delete currentErrors[errorKey];
      }

      const target = cloneDeep(currentUpdates[accessorialId]);
      target.meta[field] = accessorialUpdate[field];
      const accessorialUpdates = {
        ...currentUpdates,
        [accessorialId]: target,
      };
      return {
        accessorialUpdates,
        errors: { ...currentErrors, ...errorObj },
      };
    });
  };

  handleAccessorialRemove = (accessorialId) => {
    const { accessorialUpdates } = this.state;

    delete accessorialUpdates[accessorialId];
    this.setState({ accessorialUpdates }, () => {
      if (!Object.keys(this.state.accessorialUpdates).length) {
        this.handleHide();
      }
      this.props.handleAccessorialRemove(accessorialId);
    });
  };

  handleOnSubmit = () => {
    const { action, onAccessorialSubmit } = this.props;
    const errors = this.validate();

    this.setState({ disabled: true, errors });
    if (Object.keys(errors).length > 0) {
      return;
    }

    switch (action) {
      case 'approve_all':
        return onAccessorialSubmit({
          accessorials: this.generateAccessorialApprovalPayload(),
          resolution_type: 'approve',
          sendEmail: true,
        });
      case 'decline_all':
        return onAccessorialSubmit({
          ...this.generateAccessorialDeclinePayload(),
          resolution_type: 'decline',
          sendEmail: true,
        });
      case 'notify_all':
        return onAccessorialSubmit(this.generateAccessorialNotifyPayload());
      default:
        break;
    }
  };

  handleHide = () => this.props.onHide();

  validate() {
    const { t } = this.props;
    const validationErrors = {};
    const { payerChecked, payeeChecked, errors } = this.state;

    if (!payeeChecked && !payerChecked) {
      validationErrors.base = t('SELECT_ATLEAST_ONE_OPTION');
    }

    if (Object.keys(errors).length > 0) {
      validationErrors.invalidValues = t(INVALID_VALUE_ERROR_MSG, {
        userType: t('SHIPPER'),
      });
    }

    return validationErrors;
  }

  getAlertMessage(alertType) {
    return ALERT_MESSAGES[alertType] || '';
  }

  getAccessorialProcessedStatus(accessorial, ledger) {
    const isInProcessedLedger = this.checkProcessedAccessorials(
      [accessorial],
      ledger,
    )?.length;
    if (isInProcessedLedger) {
      return ledger === 'payables' ? 'PAID' : 'INVOICED';
    }
    return ledger === 'payables'
      ? accessorial.carrier_status
      : accessorial.shipper_status;
  }

  renderMismatchAlerts(
    accessorials = [],
    shipperSideDeclinedAccessorial = false,
  ) {
    const { t } = this.props;
    return accessorials.map((acc) => (
      <React.Fragment key={acc.shipment._id}>
        <tr>
          <td>{t(sluggify(acc.accessorial_type))}</td>
          <td>
            <ShipmentLink shipment={acc.shipment} />
          </td>
          <td>{t(sluggify(acc.shipment.status))}</td>
          {!shipperSideDeclinedAccessorial && (
            <td>{acc.shipment.carrier_name || '-'}</td>
          )}
          {!shipperSideDeclinedAccessorial && (
            <td>
              {acc[`${acc?.payee_type}_name`]
                ? `${acc[`${acc.payee_type}_name`] ?? ''} (${t(sluggify(acc.payee_type))})`
                : '-'}
            </td>
          )}
          {!shipperSideDeclinedAccessorial && (
            <td>{t(this.getAccessorialProcessedStatus(acc, 'payables'))}</td>
          )}
          <td>
            {!shipperSideDeclinedAccessorial
              ? t(this.getAccessorialProcessedStatus(acc, 'receivables'))
              : t(sluggify(acc?.shipper_decline_reason_code))}
          </td>
        </tr>
      </React.Fragment>
    ));
  }

  renderMismatchInfo(
    alertType,
    accessorials = [],
    shipperSideDeclinedAccessorial = false,
  ) {
    const { t } = this.props;
    const currentCount = accessorials.length;
    const alertClassName = classnames({
      'w-full': true,
      flex: true,
      'justify-between': true,
      'items-center': true,
      'space-x-2': true,
    });
    if (!accessorials || !currentCount) return <div />;
    return (
      <div className="w-full mx-auto mb-4">
        <Disclosure key="disclosure">
          {({ open }) => {
            return (
              <>
                <Disclosure.Button
                  key="disclosure-button"
                  className="focus:outline-none focus-visible:ring focus-visible:ring-yellow-500 focus-visible:ring-opacity-75 w-full"
                >
                  <Alert type="warning" className="w-full">
                    <div className="flex flex-wrap justify-between w-full">
                      {
                        <div className={alertClassName}>
                          <span className="hidden md:inline">
                            {t(this.getAlertMessage(alertType))}
                          </span>
                          {open && (
                            <ChevronDownIcon className="font-bold h-6 w-6" />
                          )}
                          {!open && (
                            <ChevronRightIcon className="font-bold h-6 w-6" />
                          )}
                        </div>
                      }
                    </div>
                  </Alert>
                </Disclosure.Button>
                <Disclosure.Panel
                  key="disclosure-panel"
                  className="bg-white rounded-md shadow text-sm max-h-80 overflow-y-scroll"
                >
                  {currentCount > 0 && (
                    <table className="default-table">
                      <thead>
                        <tr>
                          <th>{t('ACCESSORIAL')}</th>
                          <th>{t('SHIPMENT_ID')}</th>
                          <th>{t('SHIPMENT_STATUS')}</th>
                          {!shipperSideDeclinedAccessorial && (
                            <th>{t('SHIPMENT_CARRIER')}</th>
                          )}
                          {!shipperSideDeclinedAccessorial && (
                            <th>{t('ACCESSORIAL_PAYEE')}</th>
                          )}
                          {!shipperSideDeclinedAccessorial && (
                            <th>{t('PAYEE_STATUS')}</th>
                          )}
                          <th>{t('PAYER_STATUS')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {this.renderMismatchAlerts(
                          accessorials,
                          shipperSideDeclinedAccessorial,
                        )}
                      </tbody>
                    </table>
                  )}
                </Disclosure.Panel>
              </>
            );
          }}
        </Disclosure>
      </div>
    );
  }

  renderErrors() {
    const { t } = this.props;
    const { errors } = this.state;

    if (Object.keys(errors).length === 0) {
      return null;
    }

    return (
      <Alert
        type="error"
        title={t('ADDRESS_FOLLOWING_ISSUES')}
        className="mb-4"
      >
        <ul>
          {Object.values(errors).map((error, i) => (
            <li key={i}>{`${error}`}</li>
          ))}
        </ul>
      </Alert>
    );
  }

  renderMissingDocsAlert() {
    const { t } = this.props;
    const { missingDocs } = this.state;

    if (!missingDocs) {
      return null;
    }

    return (
      <Alert type="warning" className="mb-4">
        {t('MISSING_ACCESSORIAL_DOCS')}
      </Alert>
    );
  }

  renderMissingCustomerApprovalAlert() {
    const { t } = this.props;
    const { missingCustomerApproval } = this.state;
    if (!missingCustomerApproval) {
      return null;
    }

    return (
      <Alert type="warning" className="mb-4">
        {t('MISSING_PAYER_REQUIRED_DOCS')}
      </Alert>
    );
  }

  renderModalBody = (modalConfig) => {
    const {
      payeeChecked,
      payerChecked,
      accessorialInProcessedPayouts,
      accessorialInProcessedInvoices,
      accessorialsWithCarrierMismatch,
      accessorialsWithCanceledShipments,
      declinedAutoAppliedAccessorialShipperChanged,
      accessorialsWithCarrierMissing,
    } = this.state;
    const carrierMismatch = !!accessorialsWithCarrierMismatch?.length;
    const carrierMissing = !!accessorialsWithCarrierMissing?.length;
    const accessorialsWithCarrierMismatchOrMissing = (
      accessorialsWithCarrierMismatch || []
    ).concat(accessorialsWithCarrierMissing || []);
    const renderCarrierMismatchOrMissingError =
      !!accessorialsWithCarrierMismatchOrMissing?.length;
    const disableBothPayeePayer = !!accessorialsWithCanceledShipments?.length;
    const proccessedAccessorials = uniqBy([
      ...(accessorialInProcessedPayouts ?? []),
      ...(accessorialInProcessedInvoices ?? []),
    ]);
    const shipperMismatch =
      !!declinedAutoAppliedAccessorialShipperChanged?.length;
    const disablePayerResolutions =
      !!accessorialInProcessedInvoices?.length ||
      !!declinedAutoAppliedAccessorialShipperChanged?.length;
    const disablePayeeResolutions =
      !!renderCarrierMismatchOrMissingError ||
      !!accessorialInProcessedPayouts?.length;
    const carrierErrorTitle = getCarrierAccordionTitle(
      carrierMissing,
      carrierMismatch,
    );
    return (
      <div
        className="p-4 -m-4 overflow-y-auto bg-gray-100"
        style={{ maxHeight: '80vh' }}
      >
        {!!proccessedAccessorials.length &&
          this.renderMismatchInfo(
            'processed_accessorials',
            proccessedAccessorials,
          )}
        {!!disableBothPayeePayer &&
          this.renderMismatchInfo(
            'shipment_canceled',
            accessorialsWithCanceledShipments,
          )}
        {!!renderCarrierMismatchOrMissingError &&
          this.renderMismatchInfo(
            carrierErrorTitle,
            accessorialsWithCarrierMismatchOrMissing,
          )}
        {!!shipperMismatch &&
          this.renderMismatchInfo(
            `auto_applied_shipper_change_${this.props.action}`,
            declinedAutoAppliedAccessorialShipperChanged,
            true,
          )}
        {this.renderErrors()}
        {featureEnabled(CONFIGURED_FEATURES.SSR) && (
          <>
            {this.renderMissingDocsAlert()}
            {this.renderMissingCustomerApprovalAlert()}
          </>
        )}
        <p className="mb-2">{modalConfig.message}?</p>
        <ConfirmationModalBody
          action={this.props.action}
          handleSendAll={this.handleHide}
          removeAccessorial={this.handleAccessorialRemove}
          headers={modalConfig.headers}
          isShipperNotify={this.props.action === 'notify_all'}
          accessorialUpdates={this.state.accessorialUpdates}
          onAccessorialChange={this.handleAccessorialChange}
          actionsEnabled={modalConfig.actionsEnabled}
          payeeChecked={payeeChecked}
          payerChecked={payerChecked}
          onTogglePayeeChecked={this.handleTogglePayeeChecked}
          onTogglePayerChecked={this.handleTogglePayerChecked}
          accessorialsReasonCodes={this.state.accessorialsReasonCodes}
          disablePayerResolutions={disablePayerResolutions}
          disablePayeeResolutions={disablePayeeResolutions}
          disableBothPayeePayer={disableBothPayeePayer}
        />
      </div>
    );
  };

  renderModalFooter = () => {
    const { t, action } = this.props;
    return (
      <div className="flex justify-end">
        <div className="md:w-1/2">
          <div className="flex items-baseline justify-end p-4">
            <button
              className="block button secondary-action"
              onClick={this.handleHide}
            >
              {t('CANCEL')}
            </button>
            <button
              className="flex items-center button button-green"
              onClick={this.handleOnSubmit}
              disabled={
                this.state.disabled ||
                Object.keys(this.state.errors).length > 0 ||
                !this.state.accessorials.length ||
                (action === 'decline_all' &&
                  this.hasUnselectedDeclineReasonCodes())
              }
            >
              <span>{t('SUBMIT')}</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  render() {
    const { show, action } = this.props;

    const modalConfig = this.getModalConfig(action);
    return (
      <CargomaticModal
        location="middle"
        dialogClassName="modal-grid"
        show={show}
        title={modalConfig.title}
        onHide={this.handleHide}
        body={this.renderModalBody(modalConfig)}
        footer={this.renderModalFooter()}
      />
    );
  }
}

ConfirmationModal.propTypes = {
  action: PropTypes.string.isRequired,
  onAccessorialSubmit: PropTypes.func.isRequired,
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  selectedAccessorials: PropTypes.array.isRequired,
  handleAccessorialRemove: PropTypes.func.isRequired,
};

export default withTranslation()(ConfirmationModal);
